import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@finitless/shared/components";
import { useTranslation } from "react-i18next";
import { Button, Icon, NativeSelect } from "../../ui";
import UserCard from "../../ui/Users/UserCard";
import Pagination from "../../ui/Data/Pagination";
import UsersLoading from "../../ui/Users/UsersLoading";
import UsersError from "../../ui/Users/UsersError";
import CreateDriverManagerModal from "../../ui/Modals/CreateDriverManagerModal";
import {
  useDriverManagers,
  useDriverManagerStats,
  useToggleDriverManagerStatus,
} from "../../../hooks/useDriverManagers";

const ITEMS_PER_PAGE = 10;
const MAX_VISIBLE_PAGES = 5;

const DriversManagersPage = () => {
  const { t } = useTranslation("delivery");
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [enabledFilter, setEnabledFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [togglingUserId, setTogglingUserId] = useState(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Get driver managers data
  const {
    data: usersData,
    isLoading,
    error,
  } = useDriverManagers(enabledFilter, searchTerm, currentPage);

  // Get stats
  const { data: statsData } = useDriverManagerStats(enabledFilter, searchTerm);

  // Toggle status mutation
  const toggleStatusMutation = useToggleDriverManagerStatus();

  const users = usersData?.users || [];
  const pagination = usersData?.pagination || {};
  const total = statsData?.total || pagination?.total || 0;
  const enabledFilterOptions = [
    { value: "all", label: t("driversManagers.filters.all") },
    { value: "enabled", label: t("driversManagers.filters.active") },
    { value: "disabled", label: t("driversManagers.filters.inactive") },
  ];

  // Calculate totalPages
  const calculatedTotalPages = total ? Math.ceil(total / ITEMS_PER_PAGE) : 1;

  const totalPages = pagination?.totalPages || calculatedTotalPages;

  const handleEnabledFilterChange = (newEnabledFilter) => {
    setEnabledFilter(newEnabledFilter);
    setCurrentPage(1);
  };

  const handleSearchChange = (term) => {
    setSearchInput(term);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleViewDetails = (user) => {
    navigate(`/delivery/drivers-managers/${user.id}`, { state: { user } });
  };

  const handleToggleStatus = (userId, enabled) => {
    setTogglingUserId(userId);
    toggleStatusMutation.mutate(
      { userId, enabled },
      {
        onSettled: () => {
          setTogglingUserId(null);
        },
      },
    );
  };

  return (
    <div className="ordering-delivery-surface space-y-6">
      {/* Header */}
      <div className="flex flex-col justify-between sm:flex-row sm:items-center">
        <PageHeader
          title={t("driversManagers.header.title")}
          subtitle={t("driversManagers.header.subtitle")}
        />
        <div className="mt-4 flex space-x-3 sm:mt-0">
          <Button
            emphasis="solid"
            intent="brand"
            size="touch"
            type="button"
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center space-x-2 px-3 py-2 transition-colors sm:px-4"
          >
            <Icon name="Plus" className="h-4 w-4" />
            <span className="hidden sm:inline">
              {t("driversManagers.actions.createDriverManager")}
            </span>
            <span className="sm:hidden">
              {t("driversManagers.actions.create")}
            </span>
          </Button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="border-border bg-background mb-6 rounded-lg border p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* Left side - Search and Filters */}
          <div className="flex flex-1 flex-col gap-4 sm:flex-row">
            {/* Search */}
            <div className="min-w-0 flex-1">
              <div className="ordering-delivery-search relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Icon
                    name="Search"
                    className="text-muted-foreground h-5 w-5"
                  />
                </div>
                <input
                  type="text"
                  placeholder={t("driversManagers.filters.searchPlaceholder")}
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="border-border bg-card focus:ring-ring h-10 w-full rounded-lg border px-4 py-2 pr-10 pl-10 text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:outline-none"
                />
                {searchInput && (
                  <Button
                    variant="ghost"
                    onClick={handleClearSearch}
                    aria-label={t("agentTranslations:actions.clearSearch")}
                    className="bg-muted hover:bg-muted absolute top-1/2 right-2 flex h-5 w-5 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full transition-colors [&_svg]:size-3"
                    type="button"
                  >
                    <svg
                      aria-hidden="true"
                      className="text-foreground h-3 w-3 hover:text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </Button>
                )}
              </div>
            </div>

            {/* Enabled Filter */}
            <div className="flex-shrink-0">
              <NativeSelect
                aria-label={t("driversManagers.header.title")}
                value={enabledFilter}
                onChange={(e) => handleEnabledFilterChange(e.target.value)}
                className="custom-select border-border bg-card focus:ring-ring block h-10 w-full rounded-md border px-3 py-2 text-sm text-white focus:border-transparent focus:ring-2 focus:outline-none"
              >
                {enabledFilterOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </NativeSelect>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <UsersLoading />
      ) : error ? (
        <UsersError error={error} />
      ) : users.length > 0 ? (
        <>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {users.map((user) => (
              <UserCard
                key={user.id}
                user={user}
                onViewDetails={handleViewDetails}
                onToggleStatus={handleToggleStatus}
                isToggling={
                  togglingUserId === user.id && toggleStatusMutation.isPending
                }
              />
            ))}
          </div>

          {/* Pagination */}
          <div className="mt-8">
            <div className="border-border bg-background rounded-lg border p-4">
              <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
                {/* Pagination Info */}
                <div className="text-muted-foreground text-sm">
                  {t("driversManagers.pagination.showing")}{" "}
                  <span className="text-foreground font-medium">
                    {(currentPage - 1) * ITEMS_PER_PAGE + 1}
                  </span>{" "}
                  {t("driversManagers.pagination.to")}{" "}
                  <span className="text-foreground font-medium">
                    {Math.min(currentPage * ITEMS_PER_PAGE, total)}
                  </span>{" "}
                  {t("driversManagers.pagination.of")}{" "}
                  <span className="text-foreground font-medium">{total}</span>{" "}
                  {t("driversManagers.pagination.results")}
                </div>

                {/* Pagination Controls */}
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  maxVisiblePages={MAX_VISIBLE_PAGES}
                  disabled={isLoading}
                  className="mt-4 sm:mt-0"
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="py-12 text-center">
          <Icon
            name="Users"
            className="text-muted-foreground mx-auto mb-4 h-12 w-12"
          />
          <h3 className="text-foreground mt-2 text-sm font-medium">
            {t("driversManagers.empty.title")}
          </h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {searchTerm
              ? t("driversManagers.empty.searchHint")
              : t("driversManagers.empty.defaultHint")}
          </p>
        </div>
      )}

      {/* Create Driver Manager Modal */}
      <CreateDriverManagerModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </div>
  );
};

export default DriversManagersPage;
